import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, IconButton, Stack, Chip, Grid,
  Slider, FormControl, InputLabel, Select, MenuItem, Collapse,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, LinkRounded as LinkIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { driversApi, workItemsApi, projectsApi, tenantAdminApi } from '../api/services';
import type { AnnualStrategicDriver, WorkItem, Project } from '../types/models';

export default function StrategyPage() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDriver, setEditDriver] = useState<Partial<AnnualStrategicDriver> | null>(null);
  const [kpiInput, setKpiInput] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [allWorkItems, setAllWorkItems] = useState<WorkItem[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);

  // Expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Financial Year settings
  const [useFinancialYear, setUseFinancialYear] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(1);
  const [fyLabel, setFyLabel] = useState('');
  const [fyQuarter, setFyQuarter] = useState('');
  const [fyWeek, setFyWeek] = useState(0);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      setDrivers(await driversApi.getAll());
    } catch (err) {
      console.error('Failed to load drivers', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDrivers(); }, []);

  // Load work items for linked count and alignment
  useEffect(() => {
    workItemsApi.getAll().then(setAllWorkItems).catch(() => {});
  }, []);

  // Load projects for linked display under drivers
  useEffect(() => {
    projectsApi.getAll().then(setAllProjects).catch(() => {});
  }, []);

  // Load FY settings on mount
  useEffect(() => {
    tenantAdminApi.getFinancialYear().then((fy: any) => {
      setUseFinancialYear(fy.useFinancialYear);
      setFyStartMonth(fy.useFinancialYear ? fy.financialYearStartMonth : 1);
      setFyLabel(fy.currentFYLabel);
      setFyQuarter(`Q${fy.currentQuarter}`);
      setFyWeek(fy.currentFYWeek);
    }).catch(() => {});
  }, []);

  // Year-filtered drivers
  const filteredDrivers = useMemo(
    () => drivers.filter(d => d.year === selectedYear).sort((a, b) => a.displayOrder - b.displayOrder),
    [drivers, selectedYear]
  );

  // Linked work items count per driver
  const linkedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allWorkItems.forEach(wi => {
      if (wi.strategicDriverId) counts[wi.strategicDriverId] = (counts[wi.strategicDriverId] || 0) + 1;
    });
    return counts;
  }, [allWorkItems]);

  // Auto-progress: calculate from linked work items completion
  const autoProgress = useMemo(() => {
    const map: Record<string, { percent: number; total: number; done: number }> = {};
    drivers.forEach(d => {
      const linked = allWorkItems.filter(w => w.strategicDriverId === d.id);
      const done = linked.filter(w => w.status === 'Done').length;
      map[d.id] = {
        percent: linked.length > 0 ? Math.round((done / linked.length) * 100) : 0,
        total: linked.length,
        done,
      };
    });
    return map;
  }, [drivers, allWorkItems]);

  // Projects grouped by strategic driver
  const projectsByDriver = useMemo(() => {
    const map: Record<string, Project[]> = {};
    allProjects.forEach(p => {
      if (p.strategicDriverId) {
        if (!map[p.strategicDriverId]) map[p.strategicDriverId] = [];
        map[p.strategicDriverId].push(p);
      }
    });
    return map;
  }, [allProjects]);

  // Alignment summary: % of this week's items linked to a strategic driver
  const alignmentPercent = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    const startStr = startOfWeek.toISOString().split('T')[0];
    const endStr = endOfWeek.toISOString().split('T')[0];
    const weekItems = allWorkItems.filter(wi => wi.targetDate >= startStr && wi.targetDate <= endStr);
    if (weekItems.length === 0) return null;
    const aligned = weekItems.filter(wi => wi.strategicDriverId).length;
    return Math.round((aligned / weekItems.length) * 100);
  }, [allWorkItems]);

  // Available years from data
  const availableYears = useMemo(() => {
    const years = new Set(drivers.map(d => d.year));
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [drivers]);

  // FY label for a given year
  const getYearLabel = (year: number): string => {
    if (!useFinancialYear || fyStartMonth === 1) return `${year}`;
    const endYear = year + 1;
    return `FY${year % 100}/${endYear % 100}`;
  };

  // Inline progress update
  const handleProgressUpdate = async (id: string, value: number) => {
    try {
      await driversApi.update(id, { progressPercentage: value });
      setDrivers(prev => prev.map(d => d.id === id ? { ...d, progressPercentage: value } : d));
    } catch (err) {
      console.error('Failed to update progress', err);
    }
  };

  const handleSave = async () => {
    if (!editDriver?.title) return;
    try {
      if (editDriver.id) {
        await driversApi.update(editDriver.id, editDriver);
      } else {
        await driversApi.create({ ...editDriver, year: editDriver.year || new Date().getFullYear() });
      }
      setDialogOpen(false);
      setEditDriver(null);
      loadDrivers();
    } catch (err) {
      console.error('Failed to save driver', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this strategic driver?')) return;
    try {
      await driversApi.delete(id);
      loadDrivers();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const addKpi = () => {
    if (!kpiInput.trim()) return;
    setEditDriver(prev => ({
      ...prev,
      kpis: [...(prev?.kpis || []), kpiInput.trim()],
    }));
    setKpiInput('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Annual Strategic Drivers</Typography>
          <Typography variant="subtitle1">Define the 3-4 strategic themes that guide your year</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{useFinancialYear ? 'FY' : 'Year'}</InputLabel>
            <Select value={selectedYear} label={useFinancialYear ? 'FY' : 'Year'} onChange={e => setSelectedYear(Number(e.target.value))}>
              {availableYears.map(y => <MenuItem key={y} value={y}>{getYearLabel(y)}</MenuItem>)}
            </Select>
          </FormControl>
          {useFinancialYear && fyLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {fyQuarter} &middot; Week {fyWeek}
            </Typography>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
            setEditDriver({ title: '', description: '', displayOrder: drivers.length + 1, isActive: true, kpis: [], year: selectedYear, progressPercentage: 0 });
            setDialogOpen(true);
          }}>
            Add Driver
          </Button>
        </Stack>
      </Box>

      {/* Alignment summary */}
      {alignmentPercent !== null && (
        <Card sx={{ mb: 2.5, borderLeft: 3, borderColor: alignmentPercent >= 60 ? 'success.main' : alignmentPercent >= 30 ? 'warning.main' : 'error.main' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LinkIcon sx={{ color: alignmentPercent >= 60 ? 'success.main' : 'warning.main' }} />
              <Typography variant="body2">
                <strong>{alignmentPercent}%</strong> of this week's work items are aligned to a strategic driver
              </Typography>
              <LinearProgress
                variant="determinate"
                value={alignmentPercent}
                sx={{
                  flex: 1, height: 6, borderRadius: 3, maxWidth: 200, ml: 'auto',
                  bgcolor: 'rgba(255,255,255,0.06)',
                }}
                color={alignmentPercent >= 60 ? 'success' : alignmentPercent >= 30 ? 'warning' : 'error'}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2.5}>
        {filteredDrivers.map((driver) => (
          <Grid size={{ xs: 12, md: 6 }} key={driver.id}>
            <Card sx={{ height: '100%', borderLeft: 3, borderColor: driver.isActive ? 'primary.main' : 'divider', opacity: driver.isActive ? 1 : 0.6 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{driver.title}</Typography>
                    {driver.description && (
                      <Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 0.5 }}
                          onClick={() => toggleDescription(driver.id)}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Description
                          </Typography>
                          {expandedDescriptions.has(driver.id)
                            ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
                            : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
                          }
                        </Box>
                        <Collapse in={expandedDescriptions.has(driver.id)}>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {driver.description}
                          </Typography>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                  <Stack direction="row">
                    <IconButton size="small" onClick={() => { setEditDriver(driver); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(driver.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Box>

                {/* Progress slider */}
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Progress</Typography>
                    <Typography variant="caption" fontWeight={600}>{driver.progressPercentage}%</Typography>
                  </Box>
                  <Slider
                    value={driver.progressPercentage}
                    onChange={(_, val) => setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, progressPercentage: val as number } : d))}
                    onChangeCommitted={(_, val) => handleProgressUpdate(driver.id, val as number)}
                    size="small"
                    sx={{
                      height: 6, py: 0.5,
                      '& .MuiSlider-thumb': { width: 14, height: 14 },
                    }}
                  />
                  {autoProgress[driver.id] && autoProgress[driver.id].total > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Auto: {autoProgress[driver.id].percent}% ({autoProgress[driver.id].total} items, {autoProgress[driver.id].done} done)
                      {driver.progressPercentage !== autoProgress[driver.id].percent && ` \u00B7 Manual override: ${driver.progressPercentage}%`}
                    </Typography>
                  )}
                </Box>

                {driver.kpis && driver.kpis.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>KPIs</Typography>
                    {driver.kpis.map((kpi, i) => (
                      <Typography key={i} variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                        &bull; {kpi}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`#${driver.displayOrder}`} size="small" variant="outlined" />
                  <Chip label={getYearLabel(driver.year)} size="small" variant="outlined" />
                  {!driver.isActive && <Chip label="Inactive" size="small" color="default" />}
                  {linkedCounts[driver.id] > 0 && (
                    <Chip
                      icon={<LinkIcon sx={{ fontSize: 14 }} />}
                      label={`${linkedCounts[driver.id]} item${linkedCounts[driver.id] !== 1 ? 's' : ''} linked`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ '& .MuiChip-icon': { fontSize: 14 } }}
                    />
                  )}
                </Box>

                {/* Linked Projects */}
                {projectsByDriver[driver.id] && projectsByDriver[driver.id].length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                      Projects
                    </Typography>
                    <Stack spacing={0.5}>
                      {projectsByDriver[driver.id].map(project => (
                        <Box
                          key={project.id}
                          onClick={() => navigate(`/projects/${project.id}`)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1,
                            borderRadius: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
                            {project.title}
                          </Typography>
                          <Chip
                            label={project.ragStatus}
                            size="small"
                            color={project.ragStatus === 'Green' ? 'success' : project.ragStatus === 'Amber' ? 'warning' : 'error'}
                            sx={{ height: 20, fontSize: '0.65rem', minWidth: 50 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
                            {project.progressPercentage}%
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editDriver?.id ? 'Edit Strategic Driver' : 'New Strategic Driver'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={editDriver?.title || ''} onChange={e => setEditDriver(prev => ({ ...prev, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={3} value={editDriver?.description || ''} onChange={e => setEditDriver(prev => ({ ...prev, description: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <TextField label="Display Order" type="number" fullWidth value={editDriver?.displayOrder || 1} onChange={e => setEditDriver(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))} />
              <TextField label="Year" type="number" fullWidth value={editDriver?.year || new Date().getFullYear()} onChange={e => setEditDriver(prev => ({ ...prev, year: parseInt(e.target.value) }))} />
            </Stack>
            <TextField label="Progress (%)" type="number" fullWidth inputProps={{ min: 0, max: 100 }} value={editDriver?.progressPercentage || 0} onChange={e => setEditDriver(prev => ({ ...prev, progressPercentage: parseFloat(e.target.value) || 0 }))} />
            <Box>
              <Typography variant="caption" color="text.secondary">KPIs</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <TextField size="small" fullWidth placeholder="Add a KPI" value={kpiInput} onChange={e => setKpiInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKpi()} />
                <Button onClick={addKpi}>Add</Button>
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                {editDriver?.kpis?.map((kpi, i) => (
                  <Chip key={i} label={kpi} size="small" onDelete={() => setEditDriver(prev => ({ ...prev, kpis: prev?.kpis?.filter((_, idx) => idx !== i) }))} />
                ))}
              </Stack>
            </Box>
            <TextField label="Notes" fullWidth multiline rows={2} value={editDriver?.notes || ''} onChange={e => setEditDriver(prev => ({ ...prev, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
